import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CitySelect } from "@/components/CitySelect";

export default function SuperAdminFranchisesPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", assigned_city: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.superadminListFranchises().then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.assigned_city) return toast.error("City required");
    setBusy(true);
    try {
      // Password is a placeholder — server scrambles it and emails a
      // one-time set-password link. Sent long enough to satisfy the
      // model's min_length=6 guard so no schema change is needed.
      const placeholder = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      await api.superadminCreateFranchise({ ...form, password: placeholder });
      toast.success("Franchise operator invited \u2014 invite email sent");
      setOpen(false);
      setForm({ name: "", email: "", assigned_city: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  const toggle = async (r) => {
    try { await api.superadminUpdateFranchise(r.id, { is_active: !r.is_active }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const changeCity = async (r, city) => {
    try { await api.superadminUpdateFranchise(r.id, { assigned_city: city }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold font-heading">Franchise operators</h1>
          <p className="text-sm text-muted-foreground">One per city — each operator can create sub-admins scoped to the same city.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="franchise-add">Add franchise operator</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New franchise operator</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="franchise-name" />
              <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="franchise-email" />
              <CitySelect value={form.assigned_city} onChange={(v) => setForm({ ...form, assigned_city: v })} data-testid="franchise-city" />
              <p className="text-[11px] text-muted-foreground">A one-time set-password link is emailed to the operator — no password is chosen or shared here.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={busy || !form.email || !form.assigned_city}>{busy ? "Sending…" : "Send invite"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="py-3 px-4 text-left">Name</th><th className="py-3 px-4 text-left">Email</th><th className="py-3 px-4">City</th><th className="py-3 px-4">Status</th><th className="py-3 px-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">No franchise operators yet.</td></tr>
              : rows.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`franchise-row-${r.id}`}>
                  <td className="py-3 px-4 font-medium">{r.name}</td>
                  <td className="py-3 px-4 text-muted-foreground">{r.email}</td>
                  <td className="py-3 px-4"><Badge variant="outline">{r.assigned_city || "—"}</Badge></td>
                  <td className="py-3 px-4 text-center"><Badge variant={r.is_active ? "default" : "secondary"}>{r.is_active ? "Active" : "Suspended"}</Badge></td>
                  <td className="py-3 px-4 text-right">
                    <Button size="sm" variant="outline" onClick={() => toggle(r)}>{r.is_active ? "Suspend" : "Reactivate"}</Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
